/// <reference path="stripe.d.ts" />

import { Injectable } from '@angular/core';
import { AppConfig } from '../common';
import { environment } from 'environments/environment';

export class CreateStripeTokenResponse {
    constructor(status : number, response : StripeTokenResponse) {
        this.status = status;
        this.response = response;
    }
    status : number;
    response : StripeTokenResponse;
}

export class GetStripeTokenResponse {
    constructor(status : number, response : StripeTokenResponse) {
        this.status = status;
        this.response = response;
    }
    status : number;
    response : StripeTokenResponse;
}

/**
 * Server-side rendering capable Stripe implementation wrapped around Stripe.js on the client side,
 * throwing exceptions when on server-side.
 */
@Injectable()
export class StripeService {
    constructor(
        appConfig : AppConfig
    ) {
        console.log(`Stripe Key: ${environment.stripePublishableKey}`);
        this.init(environment.stripePublishableKey);
    }

    private async init(pk : string) {
        await this.loadApi();
        Stripe.setPublishableKey(pk);
    }

    get available(): boolean {
        return typeof Stripe !== 'undefined'
    }

    get applePay(): StripeApplePay {
        if (!this.available)
            return null;
        
        return Stripe.applePay;
    }

    private publishableKey : string;

    private _apiLoaded : Promise<void>;

    private async loadScript(url : string) {
        await new Promise<void>((resolve, reject) => {
            if (typeof window === 'undefined')
                throw new Error("Cannot load a script in SSR environment!");

            let scriptEl = document.createElement('script');
            scriptEl.src = url;
            scriptEl.addEventListener('load', () => resolve());
            scriptEl.addEventListener('error', () => reject());
            document.body.appendChild(scriptEl);
        });
    }

    private async loadApi(): Promise<void> {
        if (this._apiLoaded)
            return this._apiLoaded;

        if (typeof Stripe !== 'undefined')
            return;
        
        return this._apiLoaded = new Promise<void>(async (resolve, reject) => {
            if (typeof window === 'undefined')
                throw new Error("StripeService cannot be used server-side (yet)");

            await Promise.all([
                this.loadScript('https://js.stripe.com/v2/'),
                this.loadScript('https://js.stripe.com/v3/')
            ]);

            if (typeof Stripe !== 'undefined') {
                resolve();
                return;
            } else {
                reject();
                return;
            }
        });
    }

    async apiReady(): Promise<void> {
        await this.loadApi();
    }

    setPublishableKey(key: string): void {
        this.publishableKey = key; 
        if (this.available) 
            Stripe.setPublishableKey(this.publishableKey);
    }

    validateCardNumber(cardNumber: string): boolean {
        if (!this.available) 
            return false;
        
        return Stripe.validateCardNumber(cardNumber);
    }

    validateExpiry(month: string, year: string): boolean {
        if (!this.available) 
            return false;

        return Stripe.validateExpiry(month, year);
    }

    async validateCVC(cardCVC: string): Promise<boolean> {
        await this.apiReady();
        return Stripe.validateCVC(cardCVC);
    }

    async cardType(cardNumber: string): Promise<StripeCardDataBrand> {
        await this.apiReady();
        return Stripe.cardType(cardNumber);
    }

    async getToken(token: string): Promise<GetStripeTokenResponse> {

        await this.apiReady();
        return new Promise<GetStripeTokenResponse>((resolve, reject) => {
            Stripe.getToken(token, (status, response) => {
                resolve(new GetStripeTokenResponse(status, response));
            });
        })
    }

    get card(): StripeCard {
        // TODO: how to handle this with asynchronous API load
        if (!this.available)
            return null;
        return Stripe.card;
    }

    async createToken(data: StripeCardTokenData): Promise<CreateStripeTokenResponse> {
        await this.apiReady();

        return new Promise<CreateStripeTokenResponse>((resolve, reject) => {
            Stripe.createToken(data, (status, response) => {
                resolve(new CreateStripeTokenResponse(status, response));
            });
        })
    }

    async createTokenV3(element : any, options : Record<string,string>): Promise<string> {
        let result = await Stripe(this.publishableKey).createToken(element, options);

        if (result.error) {
            throw new Error(result.error);
        }

        return result.token;
    }

    get bankAccount(): StripeBankAccount {
        // TODO: how to handle this with asynchronous API load

        if (!this.available)
            return null;
        
        return Stripe.bankAccount;
    }
}